"use client";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import { useEffect, useState } from "react";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { customerNewsletterService } from "~/connectors/newsletter.connector";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

interface NewsletterSubscribeProps {
  initialEmail?: string;
  compact?: boolean;
}

export default function NewsletterSubscribe({
  initialEmail = "",
  compact = false,
}: NewsletterSubscribeProps) {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string>("");

  useEffect(() => {
    // Get current URL on client side
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
    }
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setMessage({
        type: "error",
        text: "Te rugăm să introduci o adresă de email",
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const data = await customerNewsletterService.subscribe(
        email,
        currentUrl,
        "newsletter_form",
      );

      setMessage({
        type: "success",
        text: data.alreadySubscribed
          ? "Ești deja abonat(ă) la newsletter!"
          : data.reactivated
            ? "Abonamentul tău a fost reactivat!"
            : "Te-ai abonat cu succes la newsletter!",
      });

      // Track newsletter subscription (only for new subscriptions, not reactivations)
      if (!data.alreadySubscribed && !data.reactivated) {
        trackEvent(AnalyticsEvents.NEWSLETTER_SUBSCRIBED, { email });
        setEmail("");
      }
    } catch (error) {
      console.error("Error subscribing to newsletter:", error);
      setMessage({
        type: "error",
        text: "A apărut o eroare. Te rugăm să încerci din nou.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <Box sx={{ width: "100%" }}>
        {message?.type === "success" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              borderRadius: 1,
              border: 1,
              borderColor: "success.main",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(56, 178, 172, 0.1)"
                  : "rgba(56, 178, 172, 0.08)",
              color: "success.main",
            }}
          >
            <CheckCircleIcon />
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "success.main" }}
            >
              {message.text}
            </Typography>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubscribe}
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 0 },
              flexDirection: { xs: "column", sm: "row" },
              width: "100%",
            }}
          >
            <TextField
              type="email"
              id={compact ? "newsletter-email-compact" : "newsletter-email"}
              size="small"
              placeholder="Adresa ta de email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              error={message?.type === "error"}
              helperText={message?.type === "error" ? message.text : ""}
              sx={{
                flex: 1,
                // no right border, it should visually merge with the button
                "& .MuiOutlinedInput-notchedOutline": {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
              fullWidth
            />
            <Button
              type="submit"
              variant="outlined"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} /> : <EmailIcon />
              }
              sx={{
                whiteSpace: "nowrap",
                minWidth: { xs: "100%", sm: "auto" },
                // no left border, it should visually merge with the text field
                borderTopLeftRadius: { sm: 0 },
                borderBottomLeftRadius: { sm: 0 },
              }}
            >
              Abonează-te
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubscribe}
      sx={{
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <TextField
          type="email"
          id={compact ? "newsletter-email-compact" : "newsletter-email"}
          size="small"
          placeholder="Adresa ta de email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          fullWidth
        />
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
          sx={{
            whiteSpace: "nowrap",
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          Abonează-te
        </Button>
      </Box>
      {message && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: message.type === "success" ? "success.main" : "error.main",
          }}
        >
          {message.text}
        </Typography>
      )}
    </Box>
  );
}
